import { UserOutlined } from "@ant-design/icons";
import { Button, Card, Col, Form, message, Row, Space, Typography } from "antd";
import axios from "axios";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDropzone } from "react-dropzone";
import { ErrorBoundary } from "react-error-boundary";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useHistory, useLocation } from "react-router-dom";
import BasicLayout from "../../../components/BasicLayout";
import Center from "../../../components/common/Center";
import FallBack from "../../../components/common/FallBack/FallBack";
import Notification from "../../../components/common/Notification";
import InputField from "../../../components/Form/InputField";
import Upload from "../../../components/Form/Upload";
import {
	addProductSecondaryCategory,
	getSingleProductSecondaryCategory,
	updateProductSecondaryCategory,
	updateProductSecondaryCategoryFailure,
} from "../../../store/actions/products/secondaryCategory/categories.action";
import { formatFileName } from "../../../utils/helper";
import Select from "../../../components/Form/Select";
import { getAllCategories } from "../../../store/actions/products/category/categories.action";
import { wordCount } from "../../../utils/wordCount";
import { WordCountDisplayer } from "../../../components/common/WordCountDisplayer";
import { aspectRatioDependancy } from "../../../utils/imageAspectRatio";
import { imageSizeDependancy } from "../../../utils/imageSize";

const BrandForm = (props) => {
	const history = useHistory();
	const dispatch = useDispatch();
	const primaryCategories = useSelector((state) => state.category.categories || []);
	const location = useLocation();
	const softdelete = new URLSearchParams(location.search).get("soft-delete");
	const admin = useSelector((state) => state?.admin?.admin || {});

	const [file, setFile] = useState([]);
	const { Title } = Typography;
	const [form] = Form.useForm();
	const category = useSelector((state) => state.productSecondaryCategory.category);
	const [uploadMedia, setUploadMedia] = useState("");

	const categoryId = +props.match.params.id;

	const currentLocation = props.location.pathname;

	const onImageDrop = useCallback(async (acceptedFiles, fileRejections) => {
		try {
			imageSizeDependancy(fileRejections);

			await aspectRatioDependancy(acceptedFiles, setFile);
		} catch (error) {
			const errorMessage = error?.message || "Something went wrong";
			Notification("error", errorMessage, "", "topRight");
		}
	}, []);

	const {
		getRootProps: getImageRootProps,
		getInputProps: getImageInputProps,
		isDragActive: isImageDragActive,
	} = useDropzone({
		onDrop: onImageDrop,
		accept: " image/png",
		multiple: false,
		maxSize: "1000000",
	});

	const handleError = (error) => {
		message.error(error.message);
	};

	useEffect(() => {
		if (categoryId) {
			dispatch(getSingleProductSecondaryCategory(categoryId, softdelete)).catch((err) => {
				Notification("error", err, "", "topRight");
				history.push("/products/secondary/categories");
			});
		}
	}, [categoryId, history, softdelete, dispatch]);

	const primaryCategoryOptions = useMemo(
		() =>
			primaryCategories?.map((primaryCategory) => ({
				key: `${primaryCategory.name} (${primaryCategory.code})`,
				value: primaryCategory.id,
			})),
		[primaryCategories]
	);

	useEffect(() => {
		dispatch(getAllCategories());
	}, [dispatch]);

	const onFinish = async (values) => {
		try {
			if (currentLocation === "/products/secondary/categories/add") {
				if (file.length === 0) {
					Notification("error", "Please upload a valid media", "", "topRight");
					return;
				}
				const fileName = formatFileName(
					file[0].name.split(".")[0],
					file[0].name.split(".").pop(),
					admin
				);
				const fileType = file[0].type;
				const data = { ...values, fileName, fileType };
				const resp = await dispatch(addProductSecondaryCategory(data));
				if (resp && resp.signedUrl) {
					const options = {
						headers: {
							"Content-Type": uploadMedia[0].type,
						},
					};
					try {
						await axios.put(resp.signedUrl, uploadMedia[0], options);
						history.push("/products/secondary/categories");
						Notification(
							"success",
							"Secondary Category has been succesfully added.",
							"",
							"topRight"
						);
					} catch (error) {
						const errorMessage = error.message || "Something went wrong";
						Notification("error", errorMessage, "", "top");
					}
				}
			} else {
				await wordCount(values.updateReason, 10);
				if (file.length === 0) {
					try {
						const resp = await dispatch(updateProductSecondaryCategory(categoryId, values));
						const message = resp.message || "Updated successfully";
						history.push("/products/secondary/categories");
						Notification("success", message, "", "topRight");
					} catch (err) {
						const errorMessage =
							(err?.response && err?.response?.data?.message) || "Some thing went wrong";
						dispatch(updateProductSecondaryCategoryFailure(errorMessage));
						Notification("error", "Error Occurred", errorMessage, "topRight");
					} finally {
						setFile([]);
					}
				} else {
					const fileName = formatFileName(
						file[0].name.split(".")[0],
						file[0].name.split(".").pop(),
						admin
					);
					const fileType = file[0].type;
					const data = { ...values, fileName, fileType };
					const resp = await dispatch(updateProductSecondaryCategory(categoryId, data));

					if (resp && resp.data.signedUrl) {
						const options = {
							headers: {
								"Content-Type": uploadMedia[0].type,
							},
						};
						try {
							await axios.put(resp.data.signedUrl, uploadMedia[0], options);
							history.push("/products/secondary/categories");
							Notification(
								"success",
								"Secondary Category been succesfully updated.",
								"",
								"topRight"
							);
						} catch (error) {
							const errorMessage = error.message || "Something went wrong";
							Notification("error", "", errorMessage, "topRight");
						}
					}
				}
			}
		} catch (err) {
			const errorMessage =
				(err?.response && err?.response?.data?.message) || "Something went wrong";
			return Notification("error", "Error Occurred", errorMessage, "topRight");
		}
	};

	const defaultValues = useMemo(
		() => ({
			code: categoryId ? category.code : "",
			name: categoryId ? category.name : "",
			primaryCategoryId: categoryId ? category.primaryCategoryId : undefined,
		}),
		[categoryId, category.code, category.name, category.primaryCategoryId]
	);

	useEffect(() => {
		form.setFieldsValue(defaultValues);
	}, [form, defaultValues]);

	const handleCancel = () => {
		Notification("info", "Job action has been cancelled", "", "topRight");
	};
	return (
		<BasicLayout accessLevel={props.accessLevel}>
			<ErrorBoundary FallbackComponent={FallBack} onError={handleError}>
				<Row type="flex" justify="center" align="middle" id="form-container">
					<Center height="4rem">
						{categoryId ? (
							<Title level={2} className="update-or-add-title">
								Update Secondary Category
							</Title>
						) : (
							<Title level={2} className="update-or-add-title">
								Add New Secondary Category
							</Title>
						)}
					</Center>
					<Col>
						<Card className="form-card">
							<Form
								form={form}
								name="basic"
								layout="vertical"
								onFinish={onFinish}
								initialValues={defaultValues}
							>
								<InputField
									rules={[
										{
											required: true,
										},
										{
											min: 1,
											message: "Secondary Category Name be minimum 1 character.",
										},
										{
											max: 40,
											message: "Secondary Category Name be minimum 40 character.",
										},
									]}
									label="Secondary Category Name"
									name="name"
									placeholder="Please enter secondary category name"
									prefix={<UserOutlined />}
								/>
								<Select
									rules={[{ required: true }]}
									label="Select Primary Category"
									name="primaryCategoryId"
									placeholder="Please enter primary category"
									options={primaryCategoryOptions}
								/>
								<InputField
									rules={[
										{
											required: true,
										},
										{
											min: 10,
											message: "Secondary category must be minimum 10 characters.",
										},
										{
											max: 10,
											message: "Secondary category must be maximum 10 characters.",
										},
									]}
									label="Code"
									name="code"
									placeholder="Please enter code"
									prefix={<UserOutlined />}
								/>
								<Upload
									getInputProps={getImageInputProps}
									getRootProps={getImageRootProps}
									type="image"
									isDragActive={isImageDragActive}
									file={file}
									url={categoryId ? category.url : ""}
									setUploadMedia={setUploadMedia}
								/>
								{categoryId ? (
									<WordCountDisplayer
										label="Update Reason"
										name="updateReason"
										placeholder="Please enter reason"
										rules={[
											{
												required: true,
											},
											{
												message: "update reason must be minimum 10 words.",
											},
										]}
									/>
								) : null}
								<Form.Item style={{ marginTop: "20px" }}>
									<Center height="0px">
										<Space>
											<NavLink to="/products/secondary/categories">
												<Button danger type="primary" onClick={handleCancel}>
													Cancel
												</Button>
											</NavLink>
											<Button type="primary" htmlType="submit">
												{categoryId ? "Update" : "Add"}
											</Button>
										</Space>
									</Center>
								</Form.Item>
							</Form>
						</Card>
					</Col>
				</Row>
			</ErrorBoundary>
		</BasicLayout>
	);
};

export default BrandForm;
